import { Eye, EyeOff, LinkExternal01, Loading01 } from '@ethsign/icons';
import { Button } from '@ethsign/ui';
import { shortenWalletAddress } from '@ethsign/utils-web';
import { sortBy } from 'lodash-es';

import { useState } from 'react';

import { useUserInfo } from '@/hooks';
import { ContractItem, ContractType } from '@/types';

import { dateFormat } from '@/utils/date';

import { WalletType } from '@/utils/wallet/types';

import { CopyText } from '../CopyText';
import { StepIcon, StepIconGray } from '../Icons';
import { InfoItem } from '../InfoItem';
import { NetworkTag } from '../Tag';
type Props = {
  contract: ContractItem;
  inModal?: boolean;
  className?: string;
  password?: string;
  showPassword?: (isShow: boolean) => void;
  viewMore?: () => void;
  hasMore?: boolean;
};
export const ContractDetail = (props: Props) => {
  const { user } = useUserInfo();
  const { contract, inModal, className, password, showPassword, viewMore, hasMore } = props;
  const [loading, setLoading] = useState(false);
  let recipients = sortBy(
    contract?.recipients.filter((item) => item.role === 'signer'),
    ['signingAt']
  );
  if (contract.contractType !== ContractType.contract) {
    recipients = recipients.reverse();
  }
  const Icon = password === '' ? Eye : EyeOff;
  return (
    <div className={className}>
      <InfoItem
        direction={inModal ? 'row' : 'col'}
        clasName="mb-5"
        title="Contract Name:"
        text={contract.title}
      ></InfoItem>
      {contract.encryptedContractPassword && user?.enableIdentity && (
        <InfoItem
          direction={inModal ? 'row' : 'col'}
          clasName="mb-5"
          title={inModal ? 'Password:' : 'Contract Password:'}
          text={
            <span className="flex flex-row items-center ">
              <span className={`relative top-[${password !== '' ? 0 : 2}px]`}>{password || '******'}</span>
              <Icon
                className="ml-2 cursor-pointer"
                onClick={() => {
                  if (!showPassword) return;
                  if (password === '') {
                    showPassword(true);
                  } else {
                    showPassword(false);
                  }
                }}
                size={20}
                color="#667085"
              ></Icon>
            </span>
          }
        ></InfoItem>
      )}
      <InfoItem
        direction={inModal ? 'row' : 'col'}
        clasName="mb-5"
        title="Creation Date:"
        text={dateFormat(contract.createdAt, 'YYYY-MM-DD HH:mm:ss ZZ')}
      ></InfoItem>
      {contract.enableExpiration && !!contract.expireTime && (
        <InfoItem
          direction={inModal ? 'row' : 'col'}
          clasName="mb-5"
          title="Expiration Date:"
          text={dateFormat(Number(contract.expireTime), 'YYYY-MM-DD HH:mm:ss ZZ')}
        ></InfoItem>
      )}
      <InfoItem
        direction={inModal ? 'row' : 'col'}
        clasName="mb-5"
        title="Document Key:"
        text={<CopyText text={contract.id} displayText={contract.id} />}
      ></InfoItem>
      {contract.txId && (
        <InfoItem
          direction={inModal ? 'row' : 'col'}
          clasName="mb-5"
          title="Document Storage CID:"
          text={
            <a
              className="w-full flex flex-row items-center"
              target="_blank"
              href={`https://viewblock.io/arweave/tx/${contract.txId}`}
              rel="noreferrer"
            >
              <span className="w-[250px] truncate" title={contract.txId}>
                {contract.txId}
              </span>
              <LinkExternal01
                size={20}
                color={'#667085'}
                className={'cursor-pointer ml-2 inline-block'}
              ></LinkExternal01>
            </a>
          }
        ></InfoItem>
      )}
      {contract.txId && (
        <InfoItem
          direction={inModal ? 'row' : 'col'}
          clasName="mb-5"
          title="Storage Provider:"
          text="Arweave"
        ></InfoItem>
      )}
      {!!recipients?.length && (
        <>
          {inModal ? (
            <div className="text-sm font-normal text-gray-600 mb-2">Signers:</div>
          ) : (
            <div className="text-lg font-bold text-gray-900 mb-4">
              Signing Activity
              {contract?.recipientsTotal ? (
                <span className="ml-2 px-2 pt-[2px] text-xs font-medium text-primary-700 bg-primary-50 rounded-2xl">
                  {contract?.recipientsTotal}
                </span>
              ) : null}
            </div>
          )}
        </>
      )}
      {recipients?.map((recipient, i) => (
        <div key={i} className="flex flex-row mb-7 relative">
          <div>{recipient.status === 'signed' ? <StepIcon /> : <StepIconGray />}</div>
          {recipients[i + 1]?.status === 'signed' && (
            <div className="h-10 w-[2px] rounded-sm bg-primary-600 absolute left-[11px] top-7"></div>
          )}
          <div className="ml-3">
            <div className="text-sm font-medium text-gray-700">
              {recipient.nickName || shortenWalletAddress(recipient.address, 'shorter') || recipient.email}
              {recipient.signingAt && (
                <span className="ml-2 text-xs font-normal text-gray-500">
                  {dateFormat(Number(recipient.signingAt), 'YYYY-MM-DD HH:mm:ss')}
                </span>
              )}
            </div>
            <div className="text-xs font-normal text-gray-500 mt-2  flex flex-row justify-start">
              {recipient.address ? (
                <>
                  <CopyText
                    displayText={shortenWalletAddress(recipient.address, 'normal')}
                    text={recipient.address}
                    buttonSize={16}
                  ></CopyText>
                  <span className="ml-2 align-middle font-medium">
                    <NetworkTag network={recipient.chainType as WalletType} />
                  </span>
                </>
              ) : (
                <span>{recipient.email}</span>
              )}
            </div>
          </div>
        </div>
      ))}
      {hasMore && (
        <Button
          onClick={async () => {
            if (loading) return;
            setLoading(true);
            if (viewMore) {
              try {
                await viewMore();
                setLoading(false);
              } catch (error) {
                setLoading(false);
              }
            }
          }}
          className="w-full"
          variant="outline"
        >
          {loading ? (
            <span className="flex flex-row justify-center items-center">
              <Loading01 className="animate-spin" size={16} color="#344054" />
              <span className="ml-2">Loading</span>
            </span>
          ) : (
            'View More'
          )}
        </Button>
      )}
    </div>
  );
};
